import React from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Table } from 'react-bootstrap';
import { usePromiseFetch } from '../../hooks';
import { TableLoader } from '../../components/commons/Loader';
import { ChargeStation, EChargeStationStatus } from '../../interfaces';
import { withDashboard } from '../../hoc';
import { SearchFields } from '../../components/search-box';
import AuthorizeView from '../../hoc/authorizeView';
import { ROLES } from '../../constants';
import Paginate from '../../components/paginate';

function ChargeStationsPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  const [fetching, response, error] = usePromiseFetch('/charge-stations', searchParams);

  if (fetching) return <TableLoader />;
  if (!response || error) return <Navigate to="/error" />;

  const {
    stations,
    size,
    total
  }: { stations: ChargeStation[]; size: number; total: number } = response
    ? response
    : {
        stations: [],
        size: 40,
        total: 0
      };

  const totalPage = Math.ceil(total / size);

  function getChargeStationDocksAvailabilityCount(docks: ChargeStation['docks']) {
    const enabledDocks = docks?.filter((dock) => dock.isEnabled);
    return `${enabledDocks?.length} / ${enabledDocks?.filter((dock) => dock.isAvailable).length}`;
  }

  return (
    <Container>
      <Row>
        <Col>
          <div className="head-panel d-flex">
            <div className="head-left">
              <h1 className="page-title">Charge stations</h1>
              <span className="text-muted">{total} entries found</span>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <SearchFields
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            fields={[
              { label: 'id', name: '_id' },
              { label: 'UUID', name: 'uuid' },
              {
                label: 'Enabled',
                name: 'isEnabled',
                type: 'dropDown',
                options: [
                  {
                    label: 'All',
                    value: ''
                  },
                  {
                    label: 'Active',
                    value: 'true'
                  },
                  {
                    label: 'Disabled',
                    value: 'false'
                  }
                ]
              },
              {
                label: 'Maintenance mode',
                name: 'underMaintenance',
                type: 'dropDown',
                options: [
                  {
                    label: 'All',
                    value: ''
                  },
                  {
                    label: 'Operational',
                    value: 'false'
                  },
                  {
                    label: 'In maintenance',
                    value: 'true'
                  }
                ]
              },
              {
                label: 'Charging type',
                name: 'chargingType',
                type: 'dropDown',
                options: [
                  {
                    label: 'All',
                    value: ''
                  },
                  {
                    label: 'Yatri fast charge',
                    value: 'Yatri fast Charge'
                  },
                  {
                    label: 'AC charge',
                    value: 'AC charge'
                  },
                  {
                    label: 'CCS',
                    value: 'CCS'
                  }
                ]
              }
            ]}
          />
        </Col>
      </Row>
      <div className="table-box">
        <Row>
          <Col>
            <Table responsive hover>
              <thead className="table-light">
                <tr>
                  <th>Id</th>
                  <th>UUID</th>
                  <th>Chargers</th>
                  <th>Docks availability</th>
                  <th>Status</th>
                  <th>Charge station location</th>
                  <th>Enabled</th>
                  <th>Owner</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {stations.map(
                  ({ _id, uuid, status, chargeStationLocale, isEnabled, docks, owner }) => (
                    <tr key={_id}>
                      <td>
                        <small>{_id}</small>
                      </td>
                      <td>
                        <small>{uuid}</small>
                      </td>
                      <td>
                        {docks
                          ?.map((dock) => dock.type)
                          .filter((v, i, a) => a.indexOf(v) === i)
                          .map((dockType) => (
                            <div key={dockType} className="badge rounded-pill bg-primary mx-1">
                              {dockType}
                            </div>
                          ))}
                      </td>
                      <td>{getChargeStationDocksAvailabilityCount(docks)}</td>
                      <td>
                        {status === EChargeStationStatus.online ? (
                          <div className="badge rounded-pill bg-success">Online</div>
                        ) : (
                          <div className="badge rounded-pill bg-warning">{status}</div>
                        )}
                      </td>
                      <td>
                        <small>
                          <Link
                            className="link"
                            to={`/charge-station-locations/${chargeStationLocale}`}
                          >
                            {chargeStationLocale}
                          </Link>
                        </small>
                      </td>
                      <td>
                        {isEnabled ? (
                          <div className="badge rounded-pill bg-success">Enabled</div>
                        ) : (
                          <div className="badge rounded-pill bg-danger">Disabled</div>
                        )}
                      </td>
                      <td>
                        <small>
                          <Link className="link" to={`/customers/${owner}`}>
                            {owner}
                          </Link>
                        </small>
                      </td>
                      <td>
                        <Link className="btn btn-sm" to={`/charge-stations/${_id}`}>
                          <i className="fas fa-eye"></i>
                        </Link>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
        <Row>
          <Col>
            <Paginate
              totalPage={totalPage}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
          </Col>
        </Row>
      </div>
    </Container>
  );
}

export default withDashboard(ChargeStationsPage);
